import { Box, Button, IconButton, Typography, useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import CancelIcon from '@material-ui/icons/Cancel';
import EditIcon from '@material-ui/icons/Edit';
import GetAppIcon from '@material-ui/icons/GetApp';
import { isEmpty, isNil } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useCurrentUser } from '../../../Session';
import { FullSaleTransactionDataFragment, SaleTransactionStage, UserType } from '../../../api';
import MobileAppBar from '../../../components/MobileAppBar';
import useCommonStyles from '../../../useCommonStyles';
import useVehicleValidation from '../hooks/useVehicleValidation';
import ActionCancelTransaction from './components/ActionCancelTransaction';

export type ViewSaleTransactionHeaderProps = {
    saleTransaction: FullSaleTransactionDataFragment;
    goToEdition: () => void;
    goToNewBidding: () => void;
    downloadPdf?: () => void;
    downloadExcel?: () => void;
};

const CancelIconButton = ({ onClick }: { onClick: () => void }) => {
    const commonStyles = useCommonStyles();

    return (
        <IconButton className={commonStyles.primary2} onClick={onClick}>
            <CancelIcon />
        </IconButton>
    );
};

const ViewSaleTransactionHeader = ({
    saleTransaction,
    goToEdition,
    goToNewBidding,
    downloadPdf,
    downloadExcel,
}: ViewSaleTransactionHeaderProps) => {
    const { t } = useTranslation(['saleTransactionsPage', 'common']);
    const commonStyles = useCommonStyles();
    const history = useHistory();
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const currentUser = useCurrentUser();

    const vehicleValidations = useVehicleValidation();
    const hasValidVehicle = useMemo(
        () => isEmpty(vehicleValidations.validate(saleTransaction)),
        [saleTransaction, vehicleValidations]
    );

    const visible = useMemo(
        () => ({
            cancel: currentUser.type !== UserType.Dealer && saleTransaction.stage !== SaleTransactionStage.Completed,
            download: currentUser.type !== UserType.SaleConsultant && currentUser.type !== UserType.Dealer,
            edit:
                saleTransaction.stage !== SaleTransactionStage.Completed &&
                [UserType.Admin, UserType.SaleConsultant].includes(currentUser.type),
            auction:
                saleTransaction.stage === SaleTransactionStage.Drafted &&
                isNil(saleTransaction.latestBiddingSession) &&
                [UserType.Admin, UserType.SaleConsultant].includes(currentUser.type),
        }),
        [currentUser.type, saleTransaction.stage, saleTransaction.latestBiddingSession]
    );

    if (isSmall) {
        return (
            <MobileAppBar>
                <Box zIndex={1}>
                    <IconButton className={commonStyles.primary2} onClick={history.goBack}>
                        <ArrowBackIosIcon className={commonStyles.bigFontSize} />
                    </IconButton>
                </Box>
                <Box display="flex" justifyContent="center" position="absolute" pr={4} width="100%">
                    <Typography className={commonStyles.fontWeightBold}>
                        {t('saleTransactionsPage:vehicleDetails')}
                    </Typography>
                </Box>
                <Box display="flex" ml="auto" zIndex={1}>
                    {visible.auction && (
                        <Button
                            color="secondary"
                            disabled={!hasValidVehicle}
                            onClick={goToNewBidding}
                            variant="contained"
                        >
                            {t('saleTransactionsPage:button.auction')}
                        </Button>
                    )}
                    {visible.cancel && (
                        <ActionCancelTransaction component={CancelIconButton} transaction={saleTransaction} />
                    )}
                    {visible.download && (
                        <IconButton className={commonStyles.primary2} onClick={downloadPdf}>
                            <GetAppIcon />
                        </IconButton>
                    )}
                    {visible.edit && (
                        <IconButton className={commonStyles.primary2} onClick={goToEdition}>
                            <EditIcon />
                        </IconButton>
                    )}
                </Box>
            </MobileAppBar>
        );
    }

    return (
        <Box alignItems="center" className={commonStyles.header} display="flex">
            <Typography className={commonStyles.title}>{t('saleTransactionsPage:vehicleDetails')}</Typography>
            <Box ml="auto">
                <Box className={commonStyles.buttonGroup}>
                    {visible.auction && (
                        <Button
                            color="secondary"
                            disabled={!hasValidVehicle}
                            onClick={goToNewBidding}
                            variant="contained"
                        >
                            {t('saleTransactionsPage:button.auction')}
                        </Button>
                    )}
                    {visible.cancel && <ActionCancelTransaction transaction={saleTransaction} />}
                    {visible.download && (
                        <>
                            <Button color="secondary" onClick={downloadExcel} variant="contained">
                                <GetAppIcon className={commonStyles.normalFontSize} fontSize="small" />{' '}
                                {t('common:exportInExcel')}
                            </Button>
                            <Button color="secondary" onClick={downloadPdf} variant="contained">
                                <GetAppIcon className={commonStyles.normalFontSize} fontSize="small" />{' '}
                                {t('common:exportInPdf')}
                            </Button>
                        </>
                    )}
                    {visible.edit && (
                        <Button color="secondary" onClick={goToEdition} variant="contained">
                            {t('common:edit')}
                        </Button>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default ViewSaleTransactionHeader;
